import { t } from 'i18next'
import { useCallback } from 'react'
import { ReactComponent as AddIcon } from '../../../../assets/icons/app/add.icon.svg'
import { ReactComponent as AuthorIcon } from '../../../../assets/icons/app/author.icon.svg'
import { ReactComponent as CrossIcon } from '../../../../assets/icons/app/cross.icon.svg'
import { analyticsDetailV2StateSetAuthorsInputsMenuOpen } from '../../../../store/reducers/analytics-details/analytic-details-v2.reducer'
import {
  AuthorInitialNameStyle,
  CardInputStyle,
  LabelButtonStyle,
  TitleCloseStyle,
} from '../SearchBarComponentsStyle'
import AuthorAutoCompleteSelection from './AuthorAutoCompleteSelection'
import {
  selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  selectAnalyticDetailsStateAuthorDetailsSelected,
  selectAnalyticDetailsStateAuthorsInputsMenuOpen,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import TitleV2 from '../../../analytics-details/work-mirror/TitleV2'
import { AnalyticsPermissionEnum } from '../../../../core/enums/analytics-permission.enum'
import {
  analyticDetailsV2StateGetAuthorDetailsData,
  analyticDetailsV2StateRemoveAuthorInputAndGetAuthorRecoV3Action,
} from '../../../../store/actions/analytics-details/analytic-details-v2.actions'
import { AuthorMirrorAuthorInterface } from '../../../../core/interface/report/author-mirror-author.interface'
import { getInitial } from '../../../../core/utils/get-author-initials'

type Props = {
  permission: AnalyticsPermissionEnum | undefined
}

const AuthorsInput = ({ permission }: Props) => {
  const authorsInputsMenuOpen = useAppSelector(
    selectAnalyticDetailsStateAuthorsInputsMenuOpen,
  )

  const authorsInput = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  )

  const dispatch = useAppDispatch()
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    dispatch(analyticsDetailV2StateSetAuthorsInputsMenuOpen(true))
  }

  const handleRemoveAuthor = (authorId: string) => {
    void dispatch(
      analyticDetailsV2StateRemoveAuthorInputAndGetAuthorRecoV3Action({
        authorId,
      }),
    )
  }

  const authorDetailsSelected = useAppSelector(
    selectAnalyticDetailsStateAuthorDetailsSelected,
  )

  const handleAuthorSelected = useCallback(
    (authorDetails: AuthorMirrorAuthorInterface | null) => {
      if (!authorDetails) {
        return
      }

      void dispatch(
        analyticDetailsV2StateGetAuthorDetailsData({ authorDetails }),
      )
    },
    [authorDetailsSelected],
  )

  return (
    <div>
      <div className="flex flex-col gap-1 pb-2">
        {authorsInput.map((author) => {
          if (!authorsInputsMenuOpen) {
            return (
              <div
                key={`author-input-miature-${author.id}`}
                className="flex flex-row items-center justify-start gap-2 px-2"
              >
                <AuthorIcon className="w-5 h-5 text-white" />
                <TitleCloseStyle>{author.name}</TitleCloseStyle>
              </div>
            )
          }

          return (
            <CardInputStyle
              key={`author-input-card-${author.id}`}
              isSelected={authorDetailsSelected?.author_id === author.id}
              className="relative flex flex-row items-center justify-start gap-2"
              onClick={(event: React.MouseEvent) => {
                event.preventDefault()
                handleAuthorSelected({
                  author,
                  author_id: author.id,
                  popularity: 0,
                })
              }}
            >
              <div className="flex-auto flex flex-row items-center gap-1">
                <AuthorInitialNameStyle>
                  {getInitial(author.name)}
                </AuthorInitialNameStyle>
                <TitleV2 title={author?.name} />
              </div>
              {(permission === AnalyticsPermissionEnum.EDIT && (
                <button
                  type="button"
                  className="absolute top-4 right-2 text-black"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    handleRemoveAuthor(author.id)
                  }}
                >
                  <CrossIcon className="w-3 h-3" />
                </button>
              )) ||
                null}
            </CardInputStyle>
          )
        })}
      </div>

      {authorsInputsMenuOpen ? (
        <AuthorAutoCompleteSelection />
      ) : (
        <div className="flex flex-row w-full">
          <LabelButtonStyle
            type="button"
            className="flex flex-row justify-start items-center gap-1"
            onClick={handleClick}
          >
            <AddIcon className="w-4 h-4" />
            <span>{t('webApp.search.authors.addAuthors')}</span>
          </LabelButtonStyle>
        </div>
      )}
    </div>
  )
}

export default AuthorsInput
